import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft, Edit, Trash2, Rocket, RefreshCw, Box, GitBranch,
  Globe, Clock, Plus, Key, History, Play, Square, RotateCcw, FileText,
  Settings, Shield, ShieldCheck, ShieldX, X, Eye, EyeOff, Copy, Check, Code, Upload, FolderSearch, Loader2,
  Folder, ChevronUp, Home, Minimize2, Maximize2, ExternalLink, MapPin, FileCode, Server
} from 'lucide-react';
import { appsApi, AppStatus, DeploymentStatus, Environment, Secret, JobStatus, SecretActivity, AppDomain, AppSSLCertificate } from '../../lib/api/apps';
import { serversApi } from '../../lib/api/servers';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { ConfirmModal } from '../../components/ui/ConfirmModal';
import { toast } from '../../stores/toastStore';
import * as Sentry from '@sentry/react';

const statusColors: Record<AppStatus, string> = {
  active: 'bg-green-100 text-green-700',
  inactive: 'bg-gray-100 text-gray-700',
  deploying: 'bg-blue-100 text-blue-700',
  failed: 'bg-red-100 text-red-700',
  maintenance: 'bg-yellow-100 text-yellow-700',
};

const deploymentStatusColors: Record<DeploymentStatus, string> = {
  pending: 'bg-gray-100 text-gray-700',
  in_progress: 'bg-blue-100 text-blue-700',
  success: 'bg-green-100 text-green-700',
  failed: 'bg-red-100 text-red-700',
  rolled_back: 'bg-yellow-100 text-yellow-700',
};

export function AppDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Track last error time for job polling throttling
  const lastJobPollingErrorTime = useRef(0);

  // Modal states
  const [showDeployModal, setShowDeployModal] = useState(false);
  const [showLogsModal, setShowLogsModal] = useState(false);
  const [showEnvModal, setShowEnvModal] = useState(false);
  const [showSecretModal, setShowSecretModal] = useState(false);
  const [showComposeModal, setShowComposeModal] = useState(false);
  const [showImportEnvModal, setShowImportEnvModal] = useState(false);
  const [importingSecrets, setImportingSecrets] = useState(false);
  const [selectedEnv, setSelectedEnv] = useState<Environment | null>(null);
  const [selectedEnvVars, setSelectedEnvVars] = useState<Record<string, boolean>>({});
  const [envFilePath, setEnvFilePath] = useState('');
  const [envFilePathInput, setEnvFilePathInput] = useState('');
  const [showEnvFileBrowser, setShowEnvFileBrowser] = useState(false);
  const [envBrowsePath, setEnvBrowsePath] = useState('/home/ubuntu');
  const [envBrowseFolders, setEnvBrowseFolders] = useState<string[]>([]);
  const [envBrowseFiles, setEnvBrowseFiles] = useState<string[]>([]);
  const [envBrowseParent, setEnvBrowseParent] = useState('/');
  const [envBrowseLoading, setEnvBrowseLoading] = useState(false);
  const [envBrowseError, setEnvBrowseError] = useState<string | null>(null);

  // Rebuild modal states
  const [showRebuildModal, setShowRebuildModal] = useState(false);
  const [rebuildJobId, setRebuildJobId] = useState<string | null>(null);
  const [rebuildStatus, setRebuildStatus] = useState<JobStatus | null>(null);
  const [rebuildPolling, setRebuildPolling] = useState(false);
  const [rebuildMinimized, setRebuildMinimized] = useState(false);
  const [rebuildApiRestarting, setRebuildApiRestarting] = useState(false);
  const [rebuildFailCount, setRebuildFailCount] = useState(0);

  // Secrets sync & activity states
  const [syncing, setSyncing] = useState(false);
  const [activityLog, setActivityLog] = useState<SecretActivity[]>([]);
  const [showActivityLog, setShowActivityLog] = useState(false);

  // Env file viewer modal
  const [showEnvFileViewerModal, setShowEnvFileViewerModal] = useState(false);
  const [envFileViewerContent, setEnvFileViewerContent] = useState('');
  const [envFileViewerLoading, setEnvFileViewerLoading] = useState(false);

  // Form states
  const [deployVersion, setDeployVersion] = useState('');
  const [newEnvName, setNewEnvName] = useState('');
  const [newSecret, setNewSecret] = useState({ key: '', value: '', description: '' });
  const [showSecretValue, setShowSecretValue] = useState<Record<string, boolean>>({});
  const [revealedSecrets, setRevealedSecrets] = useState<Record<string, string>>({});
  const [copiedSecret, setCopiedSecret] = useState<string | null>(null);
  const [editingSecret, setEditingSecret] = useState<{ id: string; key: string; value: string; description: string } | null>(null);
  const [editingEnv, setEditingEnv] = useState<{ id: string; name: string } | null>(null);

  // Selection states
  const [selectedSecrets, setSelectedSecrets] = useState<Set<string>>(new Set());
  const [deletingSelected, setDeletingSelected] = useState(false);

  // Confirm modal states
  const [confirmDeleteApp, setConfirmDeleteApp] = useState(false);
  const [confirmDeleteEnv, setConfirmDeleteEnv] = useState<Environment | null>(null);
  const [confirmDeleteSecret, setConfirmDeleteSecret] = useState<Secret | null>(null);
  const [confirmDeleteSelected, setConfirmDeleteSelected] = useState(false);

  // Domain setup states
  const [showDomainSetupModal, setShowDomainSetupModal] = useState(false);
  const [domainSetupData, setDomainSetupData] = useState({ domain: '', upstream_port: 8000, ssl_enabled: true });
  const [domainSetupLoading, setDomainSetupLoading] = useState(false);

  // Maintenance mode states
  const [newWhitelistIp, setNewWhitelistIp] = useState('');
  const [maintenanceMessage, setMaintenanceMessage] = useState('');

  // Queries
  const { data: app, isLoading, refetch: refetchApp } = useQuery({
    queryKey: ['app', id],
    queryFn: () => appsApi.get(id!),
  });

  const { data: containerStatus, refetch: refetchStatus } = useQuery({
    queryKey: ['app-status', id],
    queryFn: () => appsApi.getStatus(id!),
    refetchInterval: 10000,
    enabled: !!id && app?.app_type === 'container',  // Only poll for container apps
  });

  const { data: deployments } = useQuery({
    queryKey: ['app-deployments', id],
    queryFn: () => appsApi.listDeployments(id!),
  });

  const { data: environments, refetch: refetchEnvs } = useQuery({
    queryKey: ['app-environments', id],
    queryFn: () => appsApi.listEnvironments(id!),
  });

  const { data: secrets, refetch: refetchSecrets } = useQuery({
    queryKey: ['app-secrets', id, selectedEnv?.id],
    queryFn: () => appsApi.listSecrets(id!, selectedEnv!.id),
    enabled: !!selectedEnv,
  });

  const { data: logsData, refetch: refetchLogs } = useQuery({
    queryKey: ['app-logs', id],
    queryFn: () => appsApi.getLogs(id!, 200),
    enabled: showLogsModal,
  });

  const { data: composeData, refetch: refetchCompose } = useQuery({
    queryKey: ['app-compose', id],
    queryFn: () => appsApi.getComposeFile(id!),
    enabled: showComposeModal,
  });

  const { data: envFileData, isLoading: envFileLoading, refetch: refetchEnvFile } = useQuery({
    queryKey: ['app-env-file', id, envFilePath],
    queryFn: () => appsApi.getEnvFile(id!, envFilePath || undefined),
    enabled: showImportEnvModal && !!envFilePath,
  });

  // Domain queries
  const { data: appDomain, refetch: refetchDomain } = useQuery({
    queryKey: ['app-domain', id],
    queryFn: () => appsApi.getDomain(id!),
    enabled: !!id,
  });

  const { data: appSSLCert, refetch: refetchSSL } = useQuery({
    queryKey: ['app-ssl', id],
    queryFn: () => appsApi.getSSLCertificate(id!),
    enabled: !!id,
  });

  // App domains (multiple locations)
  const { data: appDomains, refetch: refetchDomains } = useQuery({
    queryKey: ['app-domains', id],
    queryFn: () => appsApi.getDomains(id!),
    enabled: !!id,
  });

  // Mutations
  const deleteApp = useMutation({
    mutationFn: () => appsApi.delete(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['apps'] });
      toast.success('App deleted successfully');
      navigate('/apps');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error?.message || 'Failed to delete app');
    },
  });

  const updateApp = useMutation({
    mutationFn: (data: Parameters<typeof appsApi.update>[1]) => appsApi.update(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['app', id] });
      toast.success('App updated successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error?.message || 'Failed to update app');
    },
  });

  const deployApp = useMutation({
    mutationFn: (version: string) => appsApi.deploy(id!, { version }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['app-deployments', id] });
      setShowDeployModal(false);
      setDeployVersion('');
      if (data.status === 'success') {
        toast.success('Deployment completed successfully');
      } else if (data.status === 'failed') {
        toast.error('Deployment failed');
      } else {
        toast.info('Deployment started');
      }
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error?.message || 'Failed to start deployment');
    },
  });

  const fullDeploy = useMutation({
    mutationFn: () => appsApi.deployApp(id!),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['app-deployments', id] });
      refetchStatus();
      if (data.success) {
        toast.success('Deploy completed successfully');
      } else {
        toast.error(data.error || 'Deploy failed');
      }
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error?.message || 'Failed to deploy app');
    },
  });

  const rebuildApp = useMutation({
    mutationFn: () => appsApi.rebuildApp(id!),
    onSuccess: (data) => {
      // Open modal and start polling
      setRebuildJobId(data.job_id);
      setRebuildStatus({ job_id: data.job_id, status: 'running', started_at: null, completed_at: null, output: [] });
      setShowRebuildModal(true);
      setRebuildPolling(true);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error?.message || 'Failed to start rebuild');
    },
  });

  const rollbackDeployment = useMutation({
    mutationFn: (deploymentId: string) => appsApi.rollback(id!, deploymentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['app-deployments', id] });
      toast.success('Rollback initiated');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error?.message || 'Failed to rollback');
    },
  });

  const startApp = useMutation({
    mutationFn: () => appsApi.startApp(id!),
    onSuccess: (data) => {
      refetchStatus();
      queryClient.invalidateQueries({ queryKey: ['app', id] });
      if (data.success) {
        toast.success('Container started');
      } else {
        toast.error(data.message || 'Failed to start container');
      }
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error?.message || 'Failed to start container');
    },
  });

  const stopApp = useMutation({
    mutationFn: () => appsApi.stopApp(id!),
    onSuccess: (data) => {
      refetchStatus();
      queryClient.invalidateQueries({ queryKey: ['app', id] });
      if (data.success) {
        toast.success('Container stopped');
      } else {
        toast.error(data.message || 'Failed to stop container');
      }
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error?.message || 'Failed to stop container');
    },
  });

  const restartApp = useMutation({
    mutationFn: () => appsApi.restartApp(id!),
    onSuccess: (data) => {
      refetchStatus();
      queryClient.invalidateQueries({ queryKey: ['app', id] });
      if (data.success) {
        toast.success('Container restarted');
      } else {
        toast.error(data.message || 'Failed to restart container');
      }
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error?.message || 'Failed to restart container');
    },
  });

  const createEnv = useMutation({
    mutationFn: (name: string) => appsApi.createEnvironment(id!, { name }),
    onSuccess: () => {
      refetchEnvs();
      setShowEnvModal(false);
      setNewEnvName('');
      toast.success('Environment created');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error?.message || 'Failed to create environment');
    },
  });

  const updateEnv = useMutation({
    mutationFn: (data: { envId: string; name: string }) => appsApi.updateEnvironment(id!, data.envId, { name: data.name }),
    onSuccess: (updatedEnv) => {
      refetchEnvs();
      setEditingEnv(null);
      if (selectedEnv?.id === updatedEnv.id) {
        setSelectedEnv(updatedEnv);
      }
      toast.success('Environment renamed');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error?.message || 'Failed to rename environment');
    },
  });

  const deleteEnv = useMutation({
    mutationFn: (envId: string) => appsApi.deleteEnvironment(id!, envId),
    onSuccess: () => {
      refetchEnvs();
      if (selectedEnv?.id === deleteEnv.variables) {
        setSelectedEnv(null);
      }
      toast.success('Environment deleted');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error?.message || 'Failed to delete environment');
    },
  });

  const createSecret = useMutation({
    mutationFn: (data: { key: string; value: string; description?: string }) =>
      appsApi.createSecret(id!, selectedEnv!.id, data),
    onSuccess: () => {
      refetchSecrets();
      setShowSecretModal(false);
      setNewSecret({ key: '', value: '', description: '' });
      toast.success('Secret added');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error?.message || 'Failed to add secret');
    },
  });

  const deleteSecret = useMutation({
    mutationFn: (secretId: string) => appsApi.deleteSecret(id!, selectedEnv!.id, secretId),
    onSuccess: (_data, secretId) => {
      // Remove from selection if it was selected
      setSelectedSecrets(prev => {
        const newSet = new Set(prev);
        newSet.delete(secretId);
        return newSet;
      });
      refetchSecrets();
      loadActivityLog();
      toast.success('Secret deleted');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error?.message || 'Failed to delete secret');
    },
  });

  const updateSecret = useMutation({
    mutationFn: (data: { secretId: string; key?: string; value?: string; description?: string }) =>
      appsApi.updateSecret(id!, selectedEnv!.id, data.secretId, data),
    onSuccess: () => {
      refetchSecrets();
      setEditingSecret(null);
      toast.success('Secret updated');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error?.message || 'Failed to update secret');
    },
  });

  // Domain mutations
  const setupDomain = useMutation({
    mutationFn: (data: { domain: string; upstream_port: number; ssl_enabled: boolean }) =>
      appsApi.setupDomain(id!, data),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['app', id] });
      refetchDomain();
      refetchSSL();
      setShowDomainSetupModal(false);
      setDomainSetupData({ domain: '', upstream_port: 8000, ssl_enabled: true });
      if (result.success) {
        toast.success(result.message);
      } else {
        toast.error(result.message);
      }
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to setup domain');
    },
  });

  const issueSSL = useMutation({
    mutationFn: () => appsApi.issueSSL(id!),
    onSuccess: () => {
      refetchSSL();
      refetchDomain();
      toast.success('SSL certificate issued successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to issue SSL certificate');
    },
  });

  // Handlers
  const revealSecretValue = async (secretId: string) => {
    if (revealedSecrets[secretId]) {
      // Already revealed, just toggle visibility
      setShowSecretValue(prev => ({ ...prev, [secretId]: !prev[secretId] }));
      return;
    }
    try {
      const data = await appsApi.getSecretValue(id!, selectedEnv!.id, secretId);
      setRevealedSecrets(prev => ({ ...prev, [secretId]: data.value }));
      setShowSecretValue(prev => ({ ...prev, [secretId]: true }));
    } catch (error) {
      toast.error('Failed to reveal secret value');
    }
  };

  const openEditSecret = async (secret: Secret) => {
    try {
      const data = await appsApi.getSecretValue(id!, selectedEnv!.id, secret.id);
      setEditingSecret({
        id: secret.id,
        key: data.key,
        value: data.value,
        description: secret.description || '',
      });
    } catch (error) {
      toast.error('Failed to load secret for editing');
    }
  };
  const handleDelete = () => {
    setConfirmDeleteApp(true);
  };

  const handleDeploy = () => {
    if (deployVersion) {
      deployApp.mutate(deployVersion);
    }
  };

  const handleCreateEnv = () => {
    if (newEnvName) {
      createEnv.mutate(newEnvName);
    }
  };

  const handleCreateSecret = () => {
    if (newSecret.key && newSecret.value) {
      createSecret.mutate(newSecret);
    }
  };

  const copyToClipboard = (text: string, secretId: string) => {
    navigator.clipboard.writeText(text);
    setCopiedSecret(secretId);
    setTimeout(() => setCopiedSecret(null), 2000);
  };

  const handleSyncToServer = async () => {
    if (!selectedEnv) return;
    setSyncing(true);
    try {
      const result = await appsApi.syncSecretsToServer(id!, selectedEnv.id);
      toast.success(result.message);
      // Refresh activity log
      loadActivityLog();
    } catch (error: any) {
      toast.error(error.response?.data?.error?.message || 'Failed to sync secrets');
    } finally {
      setSyncing(false);
    }
  };

  const loadActivityLog = async () => {
    if (!selectedEnv) return;
    try {
      const activity = await appsApi.getSecretActivity(id!, selectedEnv.id);
      setActivityLog(activity);
    } catch (error) {
      Sentry.captureException(error, {
        tags: {
          feature: 'apps',
          operation: 'load_activity_log',
          severity: 'warning',
        },
        extra: {
          appId: id,
          environmentId: selectedEnv.id,
        },
      });
    }
  };

  const getEnvFilePath = () => {
    // First check explicit env_file_path
    if (app?.env_file_path) return app.env_file_path;
    // If we have a service_name, use the service-specific path
    if (app?.service_name) {
      return `/home/ubuntu/tch-ecosystem/${app.service_name}/.env`;
    }
    // For container apps, derive from docker_compose_path
    if (app?.docker_compose_path) {
      const composePath = app.docker_compose_path;
      if (composePath.endsWith('.yml') || composePath.endsWith('.yaml')) {
        return composePath.replace(/\/[^/]+$/, '/.env');
      }
      return composePath.replace(/\/$/, '') + '/.env';
    }
    return null;
  };

  const handleOpenEnvFile = async () => {
    const envPath = getEnvFilePath();
    if (!envPath) return;

    setShowEnvFileViewerModal(true);
    setEnvFileViewerLoading(true);
    try {
      const result = await appsApi.getEnvFile(id!, envPath);
      if (result.success && result.variables) {
        // Convert variables to env file content
        const content = result.variables.map(v => `${v.key}=${v.value}`).join('\n');
        setEnvFileViewerContent(content);
      } else {
        setEnvFileViewerContent(result.error || 'Failed to load file');
      }
    } catch (error: any) {
      setEnvFileViewerContent('Error loading file: ' + (error.response?.data?.error?.message || error.message));
    } finally {
      setEnvFileViewerLoading(false);
    }
  };

  // Load activity log when environment changes
  useEffect(() => {
    if (selectedEnv) {
      loadActivityLog();
    }
  }, [selectedEnv?.id]);

  // Clear selection when environment changes
  useEffect(() => {
    setSelectedSecrets(new Set());
  }, [selectedEnv?.id]);

  // Selection handlers
  const toggleSelectSecret = (secretId: string) => {
    setSelectedSecrets(prev => {
      const newSet = new Set(prev);
      if (newSet.has(secretId)) {
        newSet.delete(secretId);
      } else {
        newSet.add(secretId);
      }
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    if (!secrets) return;
    if (selectedSecrets.size === secrets.length) {
      setSelectedSecrets(new Set());
    } else {
      setSelectedSecrets(new Set(secrets.map(s => s.id)));
    }
  };

  const handleDeleteSelected = async () => {
    if (!selectedEnv || selectedSecrets.size === 0) return;
    setDeletingSelected(true);
    const secretIds = Array.from(selectedSecrets);
    const total = secretIds.length;
    let deleted = 0;
    let failed = 0;
    const errors: Array<{ secretId: string; error: unknown }> = [];

    try {
      // Delete sequentially to avoid overwhelming the database
      for (const secretId of secretIds) {
        try {
          await appsApi.deleteSecret(id!, selectedEnv.id, secretId);
          deleted++;
        } catch (e) {
          failed++;
          errors.push({ secretId, error: e });
        }
      }

      // Log batch errors to Sentry once if any occurred
      if (errors.length > 0) {
        Sentry.captureException(new Error(`Bulk secret deletion failed for ${errors.length} items`), {
          tags: {
            feature: 'apps',
            operation: 'bulk_delete_secrets',
            severity: 'error',
            failureCount: errors.length.toString(),
          },
          extra: {
            appId: id,
            environmentId: selectedEnv.id,
            totalAttempted: total,
            successCount: deleted,
            failureCount: failed,
            errors: errors.map(e => ({ secretId: e.secretId, error: String(e.error) })),
          },
        });
      }

      if (failed === 0) {
        toast.success(`Deleted ${deleted} secrets`);
      } else {
        toast.warning(`Deleted ${deleted} secrets, ${failed} failed`);
      }
      setSelectedSecrets(new Set());
      refetchSecrets();
      loadActivityLog();
    } catch (error: any) {
      toast.error(error.response?.data?.error?.message || 'Failed to delete secrets');
      refetchSecrets();
    } finally {
      setDeletingSelected(false);
      setConfirmDeleteSelected(false);
    }
  };

  // Load directory contents for file browser
  const loadDirectory = async (path: string) => {
    if (!app?.server_id) return;
    setEnvBrowseLoading(true);
    setEnvBrowseError(null);
    try {
      const result = await serversApi.listDirectory(app.server_id, path);
      setEnvBrowseFolders(result.folders);
      setEnvBrowseFiles(result.files);
      setEnvBrowseParent(result.parent);
      if (result.error) setEnvBrowseError(result.error);
    } catch (error: any) {
      setEnvBrowseError(error.response?.data?.error?.message || 'Failed to load directory');
    } finally {
      setEnvBrowseLoading(false);
    }
  };

  // Load directory when import modal opens
  useEffect(() => {
    if (showImportEnvModal && app?.server_id && !envFilePath) {
      loadDirectory(envBrowsePath);
    }
  }, [showImportEnvModal, app?.server_id]);

  // Auto-select environment on load (restore from localStorage or select first)
  useEffect(() => {
    if (environments && environments.length > 0 && !selectedEnv) {
      const savedEnvId = localStorage.getItem(`app-${id}-selected-env`);
      const savedEnv = savedEnvId ? environments.find(e => e.id === savedEnvId) : null;
      setSelectedEnv(savedEnv || environments[0]);
    }
  }, [environments, id]);

  // Helper to select environment and persist to localStorage
  const handleSelectEnv = (env: Environment) => {
    setSelectedEnv(env);
    localStorage.setItem(`app-${id}-selected-env`, env.id);
  };

  // Poll for rebuild job status with retry logic for API restarts
  useEffect(() => {
    if (!rebuildPolling || !rebuildJobId || !id) return;

    const isConsoleApi = app?.service_name === 'tch-console-api';
    const MAX_RETRIES = isConsoleApi ? 60 : 10; // 90s for console-api, 15s otherwise

    const pollInterval = setInterval(async () => {
      try {
        const status = await appsApi.getJobStatus(id, rebuildJobId);
        setRebuildStatus(status);
        setRebuildApiRestarting(false);
        setRebuildFailCount(0);

        if (status.status === 'success' || status.status === 'failed') {
          setRebuildPolling(false);
          refetchStatus();
          queryClient.invalidateQueries({ queryKey: ['app', id] });
          if (status.status === 'success') {
            toast.success('Rebuild completed successfully');
          } else {
            toast.error('Rebuild failed');
          }
        }
      } catch (error: any) {
        const newFailCount = rebuildFailCount + 1;
        setRebuildFailCount(newFailCount);

        // Throttle Sentry logging to once per minute to avoid spam
        const now = Date.now();
        const ERROR_THROTTLE_MS = 60000; // 1 minute
        if (now - lastJobPollingErrorTime.current > ERROR_THROTTLE_MS) {
          Sentry.captureException(error, {
            tags: {
              feature: 'apps',
              operation: 'job_polling',
              severity: 'warning',
              isConsoleApi: isConsoleApi.toString(),
            },
            extra: {
              appId: id,
              jobId: rebuildJobId,
              failCount: newFailCount,
              maxRetries: MAX_RETRIES,
              serviceName: app?.service_name,
            },
          });
          lastJobPollingErrorTime.current = now;
        }

        // Show "API restarting" message if this is the console-api being rebuilt
        if (isConsoleApi && newFailCount >= 2) {
          setRebuildApiRestarting(true);
        }

        // Give up after max retries
        if (newFailCount >= MAX_RETRIES) {
          setRebuildPolling(false);
          setRebuildApiRestarting(false);
          if (isConsoleApi) {
            toast.info('API restarted. Check the job status manually or refresh the page.');
          } else {
            toast.error('Lost connection to API. Please refresh the page.');
          }
        }
      }
    }, 1500);

    return () => clearInterval(pollInterval);
  }, [rebuildPolling, rebuildJobId, id, rebuildFailCount, app?.service_name]);

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-48" />
          <div className="h-64 bg-gray-200 rounded" />
        </div>
      </div>
    );
  }

  if (!app) {
    return (
      <div className="max-w-4xl mx-auto text-center py-12">
        <p className="text-red-600">App not found</p>
        <Button variant="link" onClick={() => navigate('/apps')}>
          Back to Apps
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={() => navigate('/apps')} className="gap-2">
          <ArrowLeft className="h-4 w-4" />
          Back to Apps
        </Button>

        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(`/apps/${id}/edit`)}
          >
            <Edit className="h-4 w-4 mr-2" />
            Edit
          </Button>
          {app.app_type === 'container' && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => rebuildApp.mutate()}
              disabled={rebuildApp.isPending}
            >
              <Settings className="h-4 w-4 mr-2" />
              Rebuild
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => fullDeploy.mutate()}
            disabled={fullDeploy.isPending}
          >
            <Rocket className="h-4 w-4 mr-2" />
            Deploy
          </Button>
          <Button variant="destructive" size="sm" onClick={handleDelete} disabled={deleteApp.isPending}>
            <Trash2 className="h-4 w-4 mr-2" />
            Delete
          </Button>
        </div>
      </div>

      {/* App Info */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${
              app.app_type === 'static'
                ? 'bg-purple-100'
                : containerStatus?.running ? 'bg-green-100' : 'bg-gray-100'
            }`}>
              {app.app_type === 'static' ? (
                <FileCode className="h-6 w-6 text-purple-600" />
              ) : (
                <Box className={`h-6 w-6 ${containerStatus?.running ? 'text-green-600' : 'text-gray-600'}`} />
              )}
            </div>
            <div>
              <CardTitle>{app.name}</CardTitle>
              <p className="text-sm text-gray-500">{app.slug}</p>
            </div>
            <div className="ml-auto flex items-center gap-2">
              {/* App Type Badge */}
              <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                app.app_type === 'static' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
              }`}>
                {app.app_type}
              </span>
              {/* Status Badge */}
              <span className={`px-3 py-1 text-sm font-medium rounded-full ${
                app.app_type === 'static'
                  ? 'bg-green-100 text-green-700'  // Static apps always show as active
                  : containerStatus?.running
                    ? 'bg-green-100 text-green-700'
                    : 'bg-gray-100 text-gray-700'
              }`}>
                {app.app_type === 'static'
                  ? 'active'
                  : containerStatus?.running ? 'running' : containerStatus?.state || app.status}
              </span>
              {app.app_type === 'container' && (
                <Button variant="ghost" size="sm" onClick={() => refetchStatus()} title="Refresh status">
                  <RefreshCw className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Container Actions - only for container apps */}
          {app.app_type === 'container' ? (
            <div className="flex flex-wrap gap-2 pb-4 border-b">
              {containerStatus?.running ? (
                <>
                  <Button variant="outline" size="sm" onClick={() => stopApp.mutate()} disabled={stopApp.isPending}>
                    <Square className="h-4 w-4 mr-2" />
                    Stop
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => restartApp.mutate()} disabled={restartApp.isPending}>
                    <RotateCcw className="h-4 w-4 mr-2" />
                    Restart
                  </Button>
                </>
              ) : (
                <Button variant="outline" size="sm" onClick={() => startApp.mutate()} disabled={startApp.isPending}>
                  <Play className="h-4 w-4 mr-2" />
                  Start
                </Button>
              )}
              <Button variant="outline" size="sm" onClick={() => setShowLogsModal(true)}>
                <FileText className="h-4 w-4 mr-2" />
                View Logs
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-2 pb-4 border-b text-sm text-gray-600">
              <Server className="h-4 w-4" />
              <span>Served by Nginx - Static files are automatically available</span>
            </div>
          )}

          <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {app.description && (
              <div className="md:col-span-2">
                <dt className="text-sm font-medium text-gray-500">Description</dt>
                <dd className="mt-1 text-gray-900">{app.description}</dd>
              </div>
            )}
            {containerStatus?.running && containerStatus.started_at && (
              <div>
                <dt className="text-sm font-medium text-gray-500">Started At</dt>
                <dd className="mt-1 text-gray-900">{new Date(containerStatus.started_at).toLocaleString()}</dd>
              </div>
            )}
            {app.domain && (
              <div>
                <dt className="text-sm font-medium text-gray-500 flex items-center gap-1">
                  <Globe className="h-4 w-4" /> Domain
                </dt>
                <dd className="mt-1">
                  <a href={`https://${app.domain}`} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">
                    {app.domain}
                  </a>
                </dd>
              </div>
            )}
            {app.app_type === 'container' && app.docker_compose_path && (
              <div>
                <dt className="text-sm font-medium text-gray-500">Compose Path</dt>
                <dd className="mt-1 text-gray-900 font-mono text-sm truncate">{app.docker_compose_path}</dd>
              </div>
            )}
            {app.app_type === 'container' && app.service_name && (
              <div>
                <dt className="text-sm font-medium text-gray-500">Service Name</dt>
                <dd className="mt-1 text-gray-900 font-mono text-sm">{app.service_name}</dd>
              </div>
            )}
            {app.app_type === 'static' && app.static_path && (
              <div className="md:col-span-2">
                <dt className="text-sm font-medium text-gray-500 flex items-center gap-1">
                  <Folder className="h-4 w-4" /> Static Files Path
                </dt>
                <dd className="mt-1 text-gray-900 font-mono text-sm">{app.static_path}</dd>
              </div>
            )}
            {app.git_repo && (
              <div>
                <dt className="text-sm font-medium text-gray-500 flex items-center gap-1">
                  <GitBranch className="h-4 w-4" /> Repository
                </dt>
                <dd className="mt-1 text-gray-900 font-mono text-sm">{app.git_repo}</dd>
              </div>
            )}
            <div>
              <dt className="text-sm font-medium text-gray-500">Branch</dt>
              <dd className="mt-1 text-gray-900">{app.git_branch}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500 flex items-center gap-1">
                <Clock className="h-4 w-4" /> Last Updated
              </dt>
              <dd className="mt-1 text-gray-900">{new Date(app.updated_at).toLocaleString()}</dd>
            </div>
          </dl>
        </CardContent>
      </Card>

      {/* Domain & SSL */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Globe className="h-5 w-5" />
              Domains ({appDomains?.length || 0})
            </span>
            <Button variant="outline" size="sm" onClick={() => {
              setDomainSetupData({ domain: '', upstream_port: 8000, ssl_enabled: true });
              setShowDomainSetupModal(true);
            }}>
              <Plus className="h-4 w-4 mr-1" />
              Add Domain
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {appDomains && appDomains.length > 0 ? (
            <div className="space-y-3">
              {/* Group by domain */}
              {(() => {
                // Group locations by domain_id
                const domainGroups = appDomains.reduce((acc, loc) => {
                  if (!acc[loc.domain_id]) {
                    acc[loc.domain_id] = [];
                  }
                  acc[loc.domain_id].push(loc);
                  return acc;
                }, {} as Record<string, typeof appDomains>);

                return Object.entries(domainGroups).map(([domainId, locations]) => (
                  <div key={domainId} className="border rounded-lg overflow-hidden">
                    <div className="p-3 bg-gray-50 border-b flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Globe className="h-4 w-4 text-gray-500" />
                        <span className="font-medium">{locations[0]?.path === '/' ?
                          `${locations[0]?.upstream_host}` :
                          'Domain'}</span>
                      </div>
                      <Link
                        to={`/domains/${domainId}`}
                        className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1"
                      >
                        View Domain <ExternalLink className="h-3 w-3" />
                      </Link>
                    </div>
                    <div className="divide-y">
                      {locations.map((location) => (
                        <div key={location.id} className="p-3 flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <MapPin className="h-4 w-4 text-gray-400" />
                            <div>
                              <code className="text-sm font-semibold bg-gray-100 px-2 py-0.5 rounded">
                                {location.path}
                              </code>
                              <span className="text-gray-400 mx-2">→</span>
                              <span className="text-sm text-gray-600">
                                {location.upstream_host}:{location.upstream_port}
                              </span>
                              {!location.is_active && (
                                <span className="ml-2 px-1.5 py-0.5 text-xs bg-gray-100 text-gray-600 rounded">
                                  Inactive
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ));
              })()}

              {/* Legacy domain display if app.domain exists but no locations */}
              {app.domain && appDomains.length === 0 && (
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div>
                    <div className="font-medium">{app.domain}</div>
                  </div>
                  <a
                    href={`https://${app.domain}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-600 hover:underline text-sm flex items-center gap-1"
                  >
                    Visit <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
              )}

              {/* SSL status */}
              {appSSLCert && appSSLCert.expires_at && (
                <p className="text-sm text-gray-500 flex items-center gap-1 mt-2">
                  <Clock className="h-4 w-4" />
                  SSL expires: {new Date(appSSLCert.expires_at).toLocaleDateString()}
                  {appSSLCert.status === 'active' && (
                    <Button
                      variant="link"
                      size="sm"
                      className="h-auto p-0 text-primary-600"
                      onClick={() => issueSSL.mutate()}
                      disabled={issueSSL.isPending}
                    >
                      (Renew)
                    </Button>
                  )}
                </p>
              )}
            </div>
          ) : app.domain ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 border rounded-lg">
                <div>
                  <div className="font-medium">{app.domain}</div>
                </div>
                <div className="flex items-center gap-2">
                  {appSSLCert ? (
                    <span className={`px-2 py-1 text-xs font-medium rounded-full flex items-center gap-1 ${
                      appSSLCert.status === 'active' ? 'bg-green-100 text-green-700' :
                      appSSLCert.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                      appSSLCert.status === 'expired' ? 'bg-red-100 text-red-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {appSSLCert.status === 'active' ? <ShieldCheck className="h-3 w-3" /> : <Shield className="h-3 w-3" />}
                      SSL {appSSLCert.status}
                    </span>
                  ) : (
                    <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-700 flex items-center gap-1">
                      <Shield className="h-3 w-3" />
                      No SSL
                    </span>
                  )}
                  <a
                    href={`https://${app.domain}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-600 hover:underline text-sm flex items-center gap-1"
                  >
                    Visit <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <Globe className="h-12 w-12 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500 mb-4">No domain configured</p>
              <p className="text-sm text-gray-400 mb-4">
                Configure domains from the Domains page and link them to this app
              </p>
              <div className="flex justify-center gap-2">
                <Button variant="outline" onClick={() => navigate('/domains')}>
                  <Globe className="h-4 w-4 mr-2" />
                  Go to Domains
                </Button>
                <Button onClick={() => setShowDomainSetupModal(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Quick Setup
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Domain Setup Modal */}
      {showDomainSetupModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Globe className="h-5 w-5" />
                Setup Domain & SSL
              </h3>
              <Button variant="ghost" size="sm" onClick={() => setShowDomainSetupModal(false)}>
                <X className="h-4 w-4" />
              </Button>
            </div>
            <div className="space-y-4">
              <Input
                label="Domain Name"
                placeholder="app.example.com"
                value={domainSetupData.domain}
                onChange={(e) => setDomainSetupData(prev => ({ ...prev, domain: e.target.value }))}
              />
              <Input
                label="Upstream Port"
                type="number"
                placeholder="8000"
                value={domainSetupData.upstream_port}
                onChange={(e) => setDomainSetupData(prev => ({ ...prev, upstream_port: parseInt(e.target.value) || 8000 }))}
              />
              <p className="text-xs text-gray-500 -mt-2">
                The port your app runs on (nginx will proxy to this port)
              </p>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  checked={domainSetupData.ssl_enabled}
                  onChange={(e) => setDomainSetupData(prev => ({ ...prev, ssl_enabled: e.target.checked }))}
                />
                <div>
                  <span className="text-sm font-medium text-gray-700">Enable SSL (Let's Encrypt)</span>
                  <p className="text-xs text-gray-500">Free automatic SSL certificate</p>
                </div>
              </label>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800">
                <strong>Note:</strong> Before proceeding, ensure your DNS is configured to point {domainSetupData.domain || 'the domain'} to this server's IP address.
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setShowDomainSetupModal(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={() => setupDomain.mutate(domainSetupData)}
                  disabled={!domainSetupData.domain || setupDomain.isPending}
                  isLoading={setupDomain.isPending}
                >
                  {domainSetupData.ssl_enabled ? (
                    <>
                      <ShieldCheck className="h-4 w-4 mr-2" />
                      Setup with SSL
                    </>
                  ) : (
                    <>
                      <Globe className="h-4 w-4 mr-2" />
                      Setup Domain
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Maintenance Mode - Only for static apps */}
      {app.app_type === 'static' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Maintenance Mode
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {/* Mode Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                <div className="flex gap-2">
                  <button
                    onClick={() => updateApp.mutate({ maintenance_mode: null })}
                    disabled={updateApp.isPending}
                    className={`px-4 py-2 rounded-lg border transition-colors ${
                      !app.maintenance_mode
                        ? 'bg-green-100 border-green-500 text-green-700'
                        : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    Live
                  </button>
                  <button
                    onClick={() => updateApp.mutate({ maintenance_mode: 'maintenance' })}
                    disabled={updateApp.isPending}
                    className={`px-4 py-2 rounded-lg border transition-colors ${
                      app.maintenance_mode === 'maintenance'
                        ? 'bg-yellow-100 border-yellow-500 text-yellow-700'
                        : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    Maintenance
                  </button>
                  <button
                    onClick={() => updateApp.mutate({ maintenance_mode: 'coming_soon' })}
                    disabled={updateApp.isPending}
                    className={`px-4 py-2 rounded-lg border transition-colors ${
                      app.maintenance_mode === 'coming_soon'
                        ? 'bg-blue-100 border-blue-500 text-blue-700'
                        : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    Coming Soon
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  {app.maintenance_mode === 'maintenance' && 'Visitors will see a maintenance page'}
                  {app.maintenance_mode === 'coming_soon' && 'Visitors will see a coming soon page'}
                  {!app.maintenance_mode && 'Site is live and accessible to everyone'}
                </p>
              </div>

              {/* Custom Message */}
              {app.maintenance_mode && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Custom Message (Optional)</label>
                  <div className="flex gap-2">
                    <textarea
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      rows={3}
                      placeholder="Enter a custom message for visitors..."
                      value={maintenanceMessage || app.maintenance_message || ''}
                      onChange={(e) => setMaintenanceMessage(e.target.value)}
                    />
                  </div>
                  <div className="flex justify-end mt-2">
                    <Button
                      size="sm"
                      onClick={() => {
                        updateApp.mutate({ maintenance_message: maintenanceMessage });
                        setMaintenanceMessage('');
                      }}
                      disabled={updateApp.isPending || (!maintenanceMessage && !app.maintenance_message)}
                    >
                      Save Message
                    </Button>
                  </div>
                </div>
              )}

              {/* IP Whitelist */}
              {app.maintenance_mode && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    IP Whitelist
                    <span className="text-xs font-normal text-gray-500 ml-2">
                      (These IPs can bypass maintenance mode)
                    </span>
                  </label>
                  <div className="flex gap-2 mb-3">
                    <Input
                      placeholder="Enter IP address (e.g., 192.168.1.1)"
                      value={newWhitelistIp}
                      onChange={(e) => setNewWhitelistIp(e.target.value)}
                      className="flex-1"
                    />
                    <Button
                      onClick={() => {
                        if (newWhitelistIp.trim()) {
                          const currentIps = app.whitelist_ips || [];
                          if (!currentIps.includes(newWhitelistIp.trim())) {
                            updateApp.mutate({ whitelist_ips: [...currentIps, newWhitelistIp.trim()] });
                          }
                          setNewWhitelistIp('');
                        }
                      }}
                      disabled={updateApp.isPending || !newWhitelistIp.trim()}
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Add
                    </Button>
                  </div>
                  {app.whitelist_ips && app.whitelist_ips.length > 0 ? (
                    <div className="space-y-2">
                      {app.whitelist_ips.map((ip, index) => (
                        <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg border">
                          <span className="font-mono text-sm">{ip}</span>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              const newIps = app.whitelist_ips!.filter((_, i) => i !== index);
                              updateApp.mutate({ whitelist_ips: newIps });
                            }}
                            disabled={updateApp.isPending}
                          >
                            <X className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 text-center py-4 bg-gray-50 rounded-lg">
                      No whitelisted IPs. All visitors will see the {app.maintenance_mode === 'maintenance' ? 'maintenance' : 'coming soon'} page.
                    </p>
                  )}
                </div>
              )}

              {/* Info box when in maintenance mode */}
              {app.maintenance_mode && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800">
                  <strong>Note:</strong> You need to configure Nginx to serve maintenance pages.
                  The maintenance settings are stored and can be used by your Nginx configuration.
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Docker Compose */}
      {app.docker_compose_path && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Code className="h-5 w-5" />
                Docker Compose
              </span>
              <Button variant="outline" size="sm" onClick={() => setShowComposeModal(true)}>
                <Eye className="h-4 w-4 mr-1" />
                View File
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="p-3 border rounded-lg bg-gray-50">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-mono text-sm">{app.docker_compose_path}</div>
                  <div className="text-xs text-gray-500 mt-1">
                    {app.service_name && `Service: ${app.service_name}`}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => rebuildApp.mutate()}
                    disabled={rebuildApp.isPending}
                  >
                    <Settings className="h-4 w-4 mr-1" />
                    Rebuild
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Environments & Secrets */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Key className="h-5 w-5" />
              Environments & Secrets
            </span>
            <Button variant="outline" size="sm" onClick={() => setShowEnvModal(true)}>
              <Plus className="h-4 w-4 mr-1" />
              Add Environment
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!environments || environments.length === 0 ? (
            <div className="text-center py-8">
              <Key className="h-12 w-12 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500 mb-4">No environments configured</p>
              <Button variant="outline" onClick={() => setShowEnvModal(true)}>
                Create First Environment
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Environment Tabs */}
              <div className="flex gap-2 border-b pb-2 flex-wrap">
                {environments.map((env) => (
                  <div
                    key={env.id}
                    className={`group flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-t-lg transition-colors ${
                      selectedEnv?.id === env.id
                        ? 'bg-primary-100 text-primary-700 border-b-2 border-primary-500'
                        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <button onClick={() => handleSelectEnv(env)}>
                      {env.name}
                    </button>
                    {selectedEnv?.id === env.id && (
                      <div className="flex items-center gap-0.5 ml-1">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingEnv({ id: env.id, name: env.name });
                          }}
                          className="p-0.5 hover:bg-primary-200 rounded"
                          title="Rename"
                        >
                          <Edit className="h-3 w-3" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setConfirmDeleteEnv(env);
                          }}
                          className="p-0.5 hover:bg-red-200 rounded text-red-500"
                          title="Delete"
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Secrets List */}
              {selectedEnv ? (
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <div>
                      <h4 className="font-medium text-gray-700">{selectedEnv.name} Secrets</h4>
                      {getEnvFilePath() && (
                        <button
                          onClick={handleOpenEnvFile}
                          className="text-xs text-blue-600 hover:text-blue-800 hover:underline font-mono mt-0.5 flex items-center gap-1"
                        >
                          <FileText className="h-3 w-3" />
                          {getEnvFilePath()}
                        </button>
                      )}
                    </div>
                    <div className="flex gap-2">
                      {selectedSecrets.size > 0 && (
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => setConfirmDeleteSelected(true)}
                          disabled={deletingSelected}
                        >
                          {deletingSelected ? (
                            <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                          ) : (
                            <Trash2 className="h-4 w-4 mr-1" />
                          )}
                          Delete ({selectedSecrets.size})
                        </Button>
                      )}
                      {app.docker_compose_path && secrets && secrets.length > 0 && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleSyncToServer}
                          disabled={syncing}
                        >
                          {syncing ? (
                            <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                          ) : (
                            <Upload className="h-4 w-4 mr-1" />
                          )}
                          Sync to Server
                        </Button>
                      )}
                      {app.docker_compose_path && (
                        <Button variant="outline" size="sm" onClick={() => setShowImportEnvModal(true)}>
                          <Upload className="h-4 w-4 mr-1" />
                          Import .env
                        </Button>
                      )}
                      <Button variant="outline" size="sm" onClick={() => setShowSecretModal(true)}>
                        <Plus className="h-4 w-4 mr-1" />
                        Add Secret
                      </Button>
                    </div>
                  </div>

                  {!secrets || secrets.length === 0 ? (
                    <p className="text-gray-500 text-center py-4">No secrets in this environment</p>
                  ) : (
                    <div className="space-y-2">
                      {/* Select All Header */}
                      <div className="flex items-center gap-3 px-3 py-2 bg-gray-50 rounded-lg border">
                        <input
                          type="checkbox"
                          checked={secrets.length > 0 && selectedSecrets.size === secrets.length}
                          onChange={toggleSelectAll}
                          className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="text-sm text-gray-600">
                          {selectedSecrets.size === 0
                            ? 'Select all'
                            : `${selectedSecrets.size} of ${secrets.length} selected`}
                        </span>
                      </div>

                      {secrets.map((secret: Secret) => (
                        <div key={secret.id} className={`p-3 border rounded-lg hover:bg-gray-50 ${selectedSecrets.has(secret.id) ? 'bg-primary-50 border-primary-200' : ''}`}>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                              <input
                                type="checkbox"
                                checked={selectedSecrets.has(secret.id)}
                                onChange={() => toggleSelectSecret(secret.id)}
                                className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 flex-shrink-0"
                              />
                              <div className="flex-1 min-w-0">
                                <div className="font-mono text-sm font-medium">{secret.key}</div>
                                <div className="font-mono text-sm text-gray-500 flex items-center gap-2 mt-1">
                                  <span className="truncate max-w-xs">
                                    {showSecretValue[secret.id] && secret.id in revealedSecrets
                                      ? (revealedSecrets[secret.id] || <span className="text-gray-400 italic">null</span>)
                                      : '••••••••••••'}
                                  </span>
                                  <button
                                    onClick={() => revealSecretValue(secret.id)}
                                    className="text-gray-400 hover:text-gray-600 flex-shrink-0"
                                    title={showSecretValue[secret.id] ? 'Hide value' : 'Show value'}
                                  >
                                    {showSecretValue[secret.id] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                  </button>
                                  <button
                                    onClick={() => {
                                      const valueToCopy = revealedSecrets[secret.id] || '';
                                      if (!valueToCopy) {
                                        toast.error('Reveal the value first to copy');
                                        return;
                                      }
                                      copyToClipboard(valueToCopy, secret.id);
                                    }}
                                    className="text-gray-400 hover:text-gray-600 flex-shrink-0"
                                    title="Copy value"
                                  >
                                    {copiedSecret === secret.id ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                                  </button>
                                </div>
                                {secret.description && (
                                  <p className="text-xs text-gray-400 mt-1">{secret.description}</p>
                                )}
                              </div>
                            </div>
                            <div className="flex gap-1 flex-shrink-0">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openEditSecret(secret)}
                                title="Edit secret"
                              >
                                <Edit className="h-4 w-4 text-gray-500" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setConfirmDeleteSecret(secret)}
                                title="Delete secret"
                              >
                                <Trash2 className="h-4 w-4 text-red-500" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Activity Log Section */}
                  <div className="mt-6 border-t pt-4">
                    <button
                      onClick={() => {
                        setShowActivityLog(!showActivityLog);
                        if (!showActivityLog) loadActivityLog();
                      }}
                      className="flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-gray-900"
                    >
                      <History className="h-4 w-4" />
                      Activity Log
                      <span className="text-xs text-gray-400">
                        ({showActivityLog ? 'hide' : 'show'})
                      </span>
                    </button>

                    {showActivityLog && (
                      <div className="mt-3 space-y-2 max-h-64 overflow-auto">
                        {activityLog.length === 0 ? (
                          <p className="text-sm text-gray-400 text-center py-4">No activity recorded</p>
                        ) : (
                          activityLog.map((activity) => (
                            <div
                              key={activity.id}
                              className="flex items-start gap-3 p-2 rounded-lg bg-gray-50 text-sm"
                            >
                              <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${
                                activity.action === 'create' ? 'bg-green-500' :
                                activity.action === 'update' ? 'bg-blue-500' :
                                activity.action === 'delete' ? 'bg-red-500' :
                                activity.action === 'sync' ? 'bg-purple-500' :
                                'bg-gray-500'
                              }`} />
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className={`px-1.5 py-0.5 text-xs rounded font-medium ${
                                    activity.action === 'create' ? 'bg-green-100 text-green-700' :
                                    activity.action === 'update' ? 'bg-blue-100 text-blue-700' :
                                    activity.action === 'delete' ? 'bg-red-100 text-red-700' :
                                    activity.action === 'sync' ? 'bg-purple-100 text-purple-700' :
                                    'bg-gray-100 text-gray-700'
                                  }`}>
                                    {activity.action}
                                  </span>
                                  {activity.secret_key && (
                                    <code className="font-mono text-xs bg-gray-200 px-1 rounded">
                                      {activity.secret_key}
                                    </code>
                                  )}
                                </div>
                                {activity.details && (
                                  <p className="text-gray-500 text-xs mt-0.5">{activity.details}</p>
                                )}
                                <div className="flex items-center gap-2 text-xs text-gray-400 mt-1">
                                  {activity.user_email && (
                                    <span>{activity.user_email}</span>
                                  )}
                                  <span>{new Date(activity.created_at).toLocaleString()}</span>
                                </div>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <p className="text-gray-500 text-center py-4">Select an environment to view secrets</p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Deployments */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <History className="h-5 w-5" />
              Deployments
            </span>
            <Button variant="outline" size="sm" onClick={() => setShowDeployModal(true)}>
              <Plus className="h-4 w-4 mr-1" />
              New Deployment
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!deployments || deployments.items.length === 0 ? (
            <div className="text-center py-8">
              <Rocket className="h-12 w-12 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500 mb-4">No deployments yet</p>
              <Button variant="outline" onClick={() => fullDeploy.mutate()} disabled={fullDeploy.isPending}>
                {fullDeploy.isPending ? 'Deploying...' : 'Deploy Now'}
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {deployments.items.map((deployment) => (
                <div
                  key={deployment.id}
                  className="p-3 border rounded-lg flex items-center justify-between"
                >
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{deployment.version}</span>
                      <span className={`px-2 py-0.5 text-xs rounded-full ${deploymentStatusColors[deployment.status]}`}>
                        {deployment.status.replace('_', ' ')}
                      </span>
                    </div>
                    {deployment.commit_message && (
                      <p className="text-sm text-gray-500 mt-1">{deployment.commit_message}</p>
                    )}
                    <p className="text-xs text-gray-400 mt-1">
                      {new Date(deployment.created_at).toLocaleString()}
                    </p>
                  </div>
                  {deployment.status === 'success' && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => rollbackDeployment.mutate(deployment.id)}
                      disabled={rollbackDeployment.isPending}
                    >
                      <RefreshCw className="h-4 w-4 mr-1" />
                      Rollback
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Deploy Modal */}
      {showDeployModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium">New Deployment</h3>
              <button onClick={() => setShowDeployModal(false)}>
                <X className="h-5 w-5 text-gray-400" />
              </button>
            </div>
            <Input
              label="Version"
              placeholder="v1.0.0"
              value={deployVersion}
              onChange={(e) => setDeployVersion(e.target.value)}
            />
            <div className="flex justify-end gap-3 mt-4">
              <Button variant="outline" onClick={() => setShowDeployModal(false)}>
                Cancel
              </Button>
              <Button onClick={handleDeploy} disabled={!deployVersion || deployApp.isPending}>
                <Rocket className="h-4 w-4 mr-2" />
                Deploy
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Logs Modal */}
      {showLogsModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium">Container Logs</h3>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => refetchLogs()}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Refresh
                </Button>
                <button onClick={() => setShowLogsModal(false)}>
                  <X className="h-5 w-5 text-gray-400" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-auto bg-gray-900 rounded-lg p-4 font-mono text-sm text-gray-100">
              {logsData?.success ? (
                <pre className="whitespace-pre-wrap break-words">{logsData.logs || 'No logs available'}</pre>
              ) : (
                <p className="text-gray-400">Failed to load logs</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Environment Modal */}
      {showEnvModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium">Add Environment</h3>
              <button onClick={() => setShowEnvModal(false)}>
                <X className="h-5 w-5 text-gray-400" />
              </button>
            </div>
            <Input
              label="Environment Name"
              placeholder="production, staging, development"
              value={newEnvName}
              onChange={(e) => setNewEnvName(e.target.value)}
            />
            <div className="flex justify-end gap-3 mt-4">
              <Button variant="outline" onClick={() => setShowEnvModal(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateEnv} disabled={!newEnvName || createEnv.isPending}>
                Create Environment
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Secret Modal */}
      {showSecretModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium">Add Secret to {selectedEnv?.name}</h3>
              <button onClick={() => setShowSecretModal(false)}>
                <X className="h-5 w-5 text-gray-400" />
              </button>
            </div>
            <div className="space-y-4">
              <Input
                label="Key"
                placeholder="DATABASE_URL"
                value={newSecret.key}
                onChange={(e) => setNewSecret(prev => ({ ...prev, key: e.target.value }))}
              />
              <Input
                label="Value"
                placeholder="postgres://..."
                value={newSecret.value}
                onChange={(e) => setNewSecret(prev => ({ ...prev, value: e.target.value }))}
              />
              <Input
                label="Description (optional)"
                placeholder="Database connection string"
                value={newSecret.description}
                onChange={(e) => setNewSecret(prev => ({ ...prev, description: e.target.value }))}
              />
            </div>
            <div className="flex justify-end gap-3 mt-4">
              <Button variant="outline" onClick={() => setShowSecretModal(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleCreateSecret}
                disabled={!newSecret.key || !newSecret.value || createSecret.isPending}
              >
                Add Secret
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Compose File Modal */}
      {showComposeModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-medium">Docker Compose</h3>
                {composeData?.path && (
                  <p className="text-sm text-gray-500 font-mono">{composeData.path}</p>
                )}
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => refetchCompose()}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Refresh
                </Button>
                <button onClick={() => setShowComposeModal(false)}>
                  <X className="h-5 w-5 text-gray-400" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-auto bg-gray-900 rounded-lg font-mono text-sm text-gray-100">
              {composeData?.success ? (
                <pre className="p-4 whitespace-pre overflow-x-auto">{composeData.content}</pre>
              ) : (
                <div className="p-4 text-red-400">
                  {composeData?.error || 'Failed to load compose file'}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Import .env Modal */}
      {showImportEnvModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-medium">Import from .env File</h3>
                <p className="text-sm text-gray-500">Browse or enter the path to the .env file</p>
              </div>
              <button onClick={() => {
                setShowImportEnvModal(false);
                setSelectedEnvVars({});
                setEnvFilePath('');
                setEnvFilePathInput('');
                setEnvBrowsePath('/home/ubuntu');
                setEnvBrowseFolders([]);
                setEnvBrowseFiles([]);
              }}>
                <X className="h-5 w-5 text-gray-400" />
              </button>
            </div>

            {/* File Explorer */}
            {!envFilePath ? (
              <div className="flex-1 flex flex-col min-h-0">
                {/* Path bar */}
                <div className="flex items-center gap-2 mb-3 p-2 bg-gray-100 rounded-lg">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setEnvBrowsePath('/home/ubuntu');
                      loadDirectory('/home/ubuntu');
                    }}
                    title="Home"
                  >
                    <Home className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      if (envBrowseParent) {
                        setEnvBrowsePath(envBrowseParent);
                        loadDirectory(envBrowseParent);
                      }
                    }}
                    disabled={envBrowsePath === '/'}
                    title="Go up"
                  >
                    <ChevronUp className="h-4 w-4" />
                  </Button>
                  <div className="flex-1 font-mono text-sm text-gray-700 truncate">
                    {envBrowsePath}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => loadDirectory(envBrowsePath)}
                    title="Refresh"
                  >
                    <RefreshCw className="h-4 w-4" />
                  </Button>
                </div>

                {/* Directory contents */}
                <div className="flex-1 border rounded-lg overflow-auto bg-white">
                  {envBrowseLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                    </div>
                  ) : envBrowseError ? (
                    <div className="p-4 text-red-600">{envBrowseError}</div>
                  ) : (
                    <div className="divide-y">
                      {/* Folders first */}
                      {envBrowseFolders.map((folder) => (
                        <button
                          key={folder}
                          type="button"
                          onClick={() => {
                            const newPath = `${envBrowsePath}/${folder}`.replace(/\/+/g, '/');
                            setEnvBrowsePath(newPath);
                            loadDirectory(newPath);
                          }}
                          className="w-full text-left p-3 hover:bg-gray-50 flex items-center gap-3"
                        >
                          <Folder className="h-5 w-5 text-yellow-500 flex-shrink-0" />
                          <span className="text-sm text-gray-900">{folder}</span>
                        </button>
                      ))}
                      {/* Then files - highlight .env files */}
                      {envBrowseFiles.map((file) => {
                        const isEnvFile = file === '.env' || file.endsWith('.env') || file.startsWith('.env.');
                        return (
                          <button
                            key={file}
                            type="button"
                            onClick={() => {
                              if (isEnvFile) {
                                const fullPath = `${envBrowsePath}/${file}`.replace(/\/+/g, '/');
                                setEnvFilePathInput(fullPath);
                                setEnvFilePath(fullPath);
                              }
                            }}
                            disabled={!isEnvFile}
                            className={`w-full text-left p-3 flex items-center gap-3 ${
                              isEnvFile
                                ? 'hover:bg-green-50 cursor-pointer'
                                : 'opacity-50 cursor-not-allowed'
                            }`}
                          >
                            <FileText className={`h-5 w-5 flex-shrink-0 ${isEnvFile ? 'text-green-600' : 'text-gray-400'}`} />
                            <span className={`text-sm ${isEnvFile ? 'text-green-700 font-medium' : 'text-gray-500'}`}>
                              {file}
                            </span>
                            {isEnvFile && (
                              <span className="ml-auto text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">
                                Select
                              </span>
                            )}
                          </button>
                        );
                      })}
                      {envBrowseFolders.length === 0 && envBrowseFiles.length === 0 && (
                        <div className="p-4 text-center text-gray-500">
                          Empty directory
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Manual path input */}
                <div className="mt-3 pt-3 border-t">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={envFilePathInput}
                      onChange={(e) => setEnvFilePathInput(e.target.value)}
                      placeholder="Or enter path manually..."
                      className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm font-mono placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                    <Button
                      onClick={() => setEnvFilePath(envFilePathInput)}
                      disabled={!envFilePathInput}
                    >
                      Load
                    </Button>
                  </div>
                </div>
              </div>
            ) : envFileLoading ? (
              <div className="flex-1 flex items-center justify-center">
                <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
              </div>
            ) : envFileData?.success ? (
              <>
                {/* Back button and file path */}
                <div className="mb-3 flex items-center gap-2 p-2 bg-gray-100 rounded-lg">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setEnvFilePath('');
                      setEnvFilePathInput('');
                      setSelectedEnvVars({});
                    }}
                  >
                    <ArrowLeft className="h-4 w-4 mr-1" />
                    Back
                  </Button>
                  <div className="flex-1 font-mono text-sm text-gray-700 truncate">
                    {envFilePath}
                  </div>
                </div>

                {(() => {
                  const existingKeys = new Set(secrets?.map(s => s.key) || []);
                  const newVarsCount = envFileData.variables.filter(v => !existingKeys.has(v.key)).length;
                  return (
                    <>
                      <div className="mb-4 flex items-center justify-between">
                        <p className="text-sm text-gray-500">
                          Found {envFileData.variables.length} variables ({newVarsCount} new, {envFileData.variables.length - newVarsCount} already exist)
                        </p>
                        <div className="flex gap-2">
                          <button
                            className="text-sm text-primary-600 hover:underline"
                            onClick={() => {
                              const all: Record<string, boolean> = {};
                              envFileData.variables.forEach((v, i) => {
                                if (!existingKeys.has(v.key)) all[i] = true;
                              });
                              setSelectedEnvVars(all);
                            }}
                          >
                            Select All New
                          </button>
                          <span className="text-gray-300">|</span>
                          <button
                            className="text-sm text-primary-600 hover:underline"
                            onClick={() => setSelectedEnvVars({})}
                          >
                            Clear
                          </button>
                        </div>
                      </div>

                      <div className="flex-1 overflow-auto border rounded-lg">
                        {envFileData.variables.map((variable, index) => {
                          const alreadyExists = existingKeys.has(variable.key);
                          return (
                            <label
                              key={index}
                              className={`flex items-center gap-3 p-3 border-b last:border-b-0 ${
                                alreadyExists
                                  ? 'opacity-50 cursor-not-allowed bg-gray-50'
                                  : `cursor-pointer hover:bg-gray-50 ${selectedEnvVars[index] ? 'bg-primary-50' : ''}`
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={!!selectedEnvVars[index]}
                                disabled={alreadyExists}
                                onChange={(e) => setSelectedEnvVars(prev => ({
                                  ...prev,
                                  [index]: e.target.checked
                                }))}
                                className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 disabled:opacity-50"
                              />
                              <div className="flex-1 min-w-0">
                                <div className="font-mono text-sm font-medium text-gray-900 flex items-center gap-2">
                                  {variable.key}
                                  {alreadyExists && (
                                    <span className="text-xs bg-yellow-100 text-yellow-700 px-1.5 py-0.5 rounded">
                                      Already exists
                                    </span>
                                  )}
                                </div>
                                <div className="font-mono text-xs text-gray-500 truncate">{variable.value}</div>
                              </div>
                            </label>
                          );
                        })}
                      </div>
                    </>
                  );
                })()}

                <div className="flex justify-between items-center mt-4">
                  <span className="text-sm text-gray-500">
                    {Object.values(selectedEnvVars).filter(Boolean).length} selected
                  </span>
                  <div className="flex gap-3">
                    <Button variant="outline" onClick={() => {
                      setShowImportEnvModal(false);
                      setSelectedEnvVars({});
                      setEnvFilePath('');
                      setEnvFilePathInput('');
                      setEnvBrowsePath('/home/ubuntu');
                      setEnvBrowseFolders([]);
                      setEnvBrowseFiles([]);
                    }}>
                      Cancel
                    </Button>
                    <Button
                      onClick={async () => {
                        const selected = envFileData.variables.filter((_, i) => selectedEnvVars[i]);
                        const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));
                        const errors: Array<{ operation: string; key: string; error: unknown }> = [];

                        setImportingSecrets(true);
                        try {
                          // Delete all existing secrets first (with small delay to prevent 503)
                          if (secrets && secrets.length > 0) {
                            for (const secret of secrets) {
                              try {
                                await appsApi.deleteSecret(id!, selectedEnv!.id, secret.id);
                                await delay(50); // Small delay between deletes
                              } catch (e) {
                                errors.push({ operation: 'delete', key: secret.key, error: e });
                              }
                            }
                          }

                          // Import new secrets (with small delay to prevent 503)
                          for (const v of selected) {
                            try {
                              await appsApi.createSecret(id!, selectedEnv!.id, {
                                key: v.key,
                                value: v.value,
                              });
                              await delay(50); // Small delay between creates
                            } catch (e) {
                              errors.push({ operation: 'import', key: v.key, error: e });
                            }
                          }

                          // Log batch errors to Sentry once if any occurred
                          if (errors.length > 0) {
                            Sentry.captureException(new Error(`Bulk secret replacement failed for ${errors.length} items`), {
                              tags: {
                                feature: 'apps',
                                operation: 'bulk_replace_secrets',
                                severity: 'error',
                                failureCount: errors.length.toString(),
                              },
                              extra: {
                                appId: id,
                                environmentId: selectedEnv!.id,
                                totalSecrets: secrets?.length || 0,
                                selectedCount: selected.length,
                                errors: errors.map(e => ({ operation: e.operation, key: e.key, error: String(e.error) })),
                              },
                            });
                          }

                          toast.success(`Replaced with ${selected.length} secrets`);
                          refetchSecrets();
                          loadActivityLog(); // Refresh activity log
                          setShowImportEnvModal(false);
                          setSelectedEnvVars({});
                          setEnvFilePath('');
                          setEnvFilePathInput('');
                          setEnvBrowsePath('/home/ubuntu');
                          setEnvBrowseFolders([]);
                          setEnvBrowseFiles([]);
                        } finally {
                          setImportingSecrets(false);
                        }
                      }}
                      disabled={Object.values(selectedEnvVars).filter(Boolean).length === 0 || importingSecrets}
                    >
                      {importingSecrets ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Importing...
                        </>
                      ) : (
                        <>
                          <Upload className="h-4 w-4 mr-2" />
                          Replace & Import
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center text-center py-8">
                <FileText className="h-12 w-12 text-gray-300 mb-4" />
                <p className="text-gray-500 mb-2">No .env file found</p>
                <p className="text-sm text-gray-400">{envFileData?.error}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Edit Secret Modal */}
      {editingSecret && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium">Edit Secret</h3>
              <button onClick={() => setEditingSecret(null)}>
                <X className="h-5 w-5 text-gray-400" />
              </button>
            </div>
            <div className="space-y-4">
              <Input
                label="Key"
                value={editingSecret.key}
                onChange={(e) => setEditingSecret(prev => prev ? { ...prev, key: e.target.value } : null)}
              />
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Value</label>
                <textarea
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 font-mono text-sm"
                  rows={4}
                  value={editingSecret.value}
                  onChange={(e) => setEditingSecret(prev => prev ? { ...prev, value: e.target.value } : null)}
                />
              </div>
              <Input
                label="Description (optional)"
                value={editingSecret.description}
                onChange={(e) => setEditingSecret(prev => prev ? { ...prev, description: e.target.value } : null)}
              />
            </div>
            <div className="flex justify-end gap-3 mt-4">
              <Button variant="outline" onClick={() => setEditingSecret(null)}>
                Cancel
              </Button>
              <Button
                onClick={() => {
                  updateSecret.mutate({
                    secretId: editingSecret.id,
                    key: editingSecret.key,
                    value: editingSecret.value,
                    description: editingSecret.description,
                  });
                }}
                disabled={!editingSecret.key || !editingSecret.value || updateSecret.isPending}
              >
                {updateSecret.isPending ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Rename Environment Modal */}
      {editingEnv && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-sm">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium">Rename Environment</h3>
              <button onClick={() => setEditingEnv(null)}>
                <X className="h-5 w-5 text-gray-400" />
              </button>
            </div>
            <Input
              label="Environment Name"
              value={editingEnv.name}
              onChange={(e) => setEditingEnv(prev => prev ? { ...prev, name: e.target.value } : null)}
              autoFocus
            />
            <div className="flex justify-end gap-3 mt-4">
              <Button variant="outline" onClick={() => setEditingEnv(null)}>
                Cancel
              </Button>
              <Button
                onClick={() => {
                  updateEnv.mutate({
                    envId: editingEnv.id,
                    name: editingEnv.name,
                  });
                }}
                disabled={!editingEnv.name || updateEnv.isPending}
              >
                {updateEnv.isPending ? 'Saving...' : 'Rename'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Delete App Modal */}
      <ConfirmModal
        isOpen={confirmDeleteApp}
        onClose={() => setConfirmDeleteApp(false)}
        onConfirm={() => {
          deleteApp.mutate();
          setConfirmDeleteApp(false);
        }}
        title="Delete Application"
        message={
          <span>
            Are you sure you want to delete <strong>{app?.name}</strong>? This action cannot be undone.
          </span>
        }
        confirmText="Delete App"
        variant="danger"
        isLoading={deleteApp.isPending}
      />

      {/* Confirm Delete Environment Modal */}
      <ConfirmModal
        isOpen={!!confirmDeleteEnv}
        onClose={() => setConfirmDeleteEnv(null)}
        onConfirm={() => {
          if (confirmDeleteEnv) {
            deleteEnv.mutate(confirmDeleteEnv.id);
            setConfirmDeleteEnv(null);
          }
        }}
        title="Delete Environment"
        message={
          <span>
            Are you sure you want to delete the <strong>{confirmDeleteEnv?.name}</strong> environment?
            All secrets in this environment will be permanently deleted.
          </span>
        }
        confirmText="Delete Environment"
        variant="danger"
        isLoading={deleteEnv.isPending}
      />

      {/* Confirm Delete Secret Modal */}
      <ConfirmModal
        isOpen={!!confirmDeleteSecret}
        onClose={() => setConfirmDeleteSecret(null)}
        onConfirm={() => {
          if (confirmDeleteSecret) {
            deleteSecret.mutate(confirmDeleteSecret.id);
            setConfirmDeleteSecret(null);
          }
        }}
        title="Delete Secret"
        message={
          <span>
            Are you sure you want to delete the secret <strong>{confirmDeleteSecret?.key}</strong>?
          </span>
        }
        confirmText="Delete Secret"
        variant="danger"
        isLoading={deleteSecret.isPending}
      />

      {/* Confirm Delete Selected Secrets Modal */}
      <ConfirmModal
        isOpen={confirmDeleteSelected}
        onClose={() => setConfirmDeleteSelected(false)}
        onConfirm={handleDeleteSelected}
        title="Delete Selected Secrets"
        message={
          <span>
            Are you sure you want to delete <strong>{selectedSecrets.size} secrets</strong>? This action cannot be undone.
          </span>
        }
        confirmText={`Delete ${selectedSecrets.size} Secrets`}
        variant="danger"
        isLoading={deletingSelected}
      />

      {/* Env File Viewer Modal */}
      {showEnvFileViewerModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <div>
                <h3 className="text-lg font-semibold">Server .env File</h3>
                <p className="text-sm text-gray-500 font-mono">{getEnvFilePath()}</p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setShowEnvFileViewerModal(false)}>
                <X className="h-4 w-4" />
              </Button>
            </div>
            <div className="flex-1 overflow-auto p-4">
              {envFileViewerLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                </div>
              ) : (
                <pre className="font-mono text-sm bg-gray-50 p-4 rounded-lg overflow-auto whitespace-pre-wrap">
                  {envFileViewerContent || 'No content'}
                </pre>
              )}
            </div>
            <div className="flex items-center justify-between p-4 border-t bg-gray-50">
              <p className="text-xs text-gray-500">
                {envFileViewerContent.split('\n').filter(l => l.trim()).length} variables in server file
              </p>
              <Button variant="outline" onClick={() => setShowEnvFileViewerModal(false)}>
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Rebuild Modal - Minimized */}
      {showRebuildModal && rebuildMinimized && (
        <div className="fixed bottom-4 right-4 z-50">
          <div className="bg-white rounded-lg shadow-lg border w-80">
            <div className="flex items-center justify-between px-4 py-3">
              <div className="flex items-center gap-3 min-w-0">
                {rebuildStatus?.status === 'running' && (
                  <Loader2 className="h-4 w-4 animate-spin text-blue-500 flex-shrink-0" />
                )}
                {rebuildStatus?.status === 'success' && (
                  <div className="w-4 h-4 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
                    <Check className="h-3 w-3 text-white" />
                  </div>
                )}
                {rebuildStatus?.status === 'failed' && (
                  <div className="w-4 h-4 rounded-full bg-red-500 flex-shrink-0" />
                )}
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">Rebuilding {app.name}</p>
                  <p className="text-xs text-gray-500">
                    {rebuildStatus?.status === 'running' && 'In progress...'}
                    {rebuildStatus?.status === 'success' && 'Completed'}
                    {rebuildStatus?.status === 'failed' && 'Failed'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <button
                  onClick={() => setRebuildMinimized(false)}
                  className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                  title="Expand"
                >
                  <Maximize2 className="h-4 w-4" />
                </button>
                <button
                  onClick={() => {
                    setShowRebuildModal(false);
                    setRebuildPolling(false);
                    setRebuildJobId(null);
                    setRebuildStatus(null);
                    setRebuildMinimized(false);
                    setRebuildApiRestarting(false);
                    setRebuildFailCount(0);
                  }}
                  disabled={rebuildPolling}
                  className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded disabled:opacity-50"
                  title="Close"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Rebuild Modal - Full */}
      {showRebuildModal && !rebuildMinimized && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-3xl max-h-[85vh] flex flex-col mx-4">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <div>
                <h3 className="text-lg font-medium">Rebuilding: {app.name}</h3>
                <p className="text-sm text-gray-500">
                  {rebuildStatus?.status === 'running' && 'Build in progress...'}
                  {rebuildStatus?.status === 'success' && 'Build completed successfully'}
                  {rebuildStatus?.status === 'failed' && 'Build failed'}
                </p>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setRebuildMinimized(true)}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                  title="Minimize"
                >
                  <Minimize2 className="h-5 w-5" />
                </button>
                <button
                  onClick={() => {
                    setShowRebuildModal(false);
                    setRebuildPolling(false);
                    setRebuildJobId(null);
                    setRebuildStatus(null);
                    setRebuildApiRestarting(false);
                    setRebuildFailCount(0);
                  }}
                  disabled={rebuildPolling}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded disabled:opacity-50"
                  title="Close"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Terminal Output */}
            <div className="flex-1 overflow-auto bg-gray-900 font-mono text-sm text-gray-100 p-4 min-h-[300px]">
              {rebuildStatus?.output && rebuildStatus.output.length > 0 ? (
                <div className="space-y-0.5">
                  {rebuildStatus.output.map((line, index) => (
                    <div
                      key={index}
                      className={`whitespace-pre-wrap break-words ${
                        line.startsWith('$') ? 'text-green-400 font-bold' :
                        line.startsWith('✓') ? 'text-green-400' :
                        line.includes('error') || line.includes('Error') || line.includes('failed') ? 'text-red-400' :
                        line.includes('warning') || line.includes('Warning') ? 'text-yellow-400' :
                        ''
                      }`}
                    >
                      {line || '\u00A0'}
                    </div>
                  ))}
                  {rebuildPolling && !rebuildApiRestarting && (
                    <div className="flex items-center gap-2 text-gray-400 mt-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span>Building...</span>
                    </div>
                  )}
                  {rebuildApiRestarting && (
                    <div className="mt-4 p-3 bg-yellow-900/50 border border-yellow-700 rounded-lg">
                      <div className="flex items-center gap-2 text-yellow-400">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span className="font-semibold">API is restarting...</span>
                      </div>
                      <p className="text-yellow-300/80 text-xs mt-1">
                        The Console API is being rebuilt. Waiting for it to come back online...
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center gap-2 text-gray-400">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Starting build...</span>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between px-6 py-4 border-t">
              <div className="flex items-center gap-2">
                {rebuildApiRestarting && (
                  <>
                    <div className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse" />
                    <span className="text-sm text-yellow-600">API Restarting...</span>
                  </>
                )}
                {!rebuildApiRestarting && rebuildStatus?.status === 'running' && (
                  <>
                    <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
                    <span className="text-sm text-gray-600">Building</span>
                  </>
                )}
                {rebuildStatus?.status === 'success' && (
                  <>
                    <div className="w-2 h-2 bg-green-500 rounded-full" />
                    <span className="text-sm text-green-600">Completed</span>
                  </>
                )}
                {rebuildStatus?.status === 'failed' && (
                  <>
                    <div className="w-2 h-2 bg-red-500 rounded-full" />
                    <span className="text-sm text-red-600">Failed</span>
                  </>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setRebuildMinimized(true)}
                >
                  <Minimize2 className="h-4 w-4 mr-1" />
                  Minimize
                </Button>
                <Button
                  variant={rebuildPolling ? 'outline' : 'default'}
                  onClick={() => {
                    setShowRebuildModal(false);
                    setRebuildPolling(false);
                    setRebuildJobId(null);
                    setRebuildStatus(null);
                    setRebuildApiRestarting(false);
                    setRebuildFailCount(0);
                  }}
                  disabled={rebuildPolling}
                >
                  {rebuildPolling ? (rebuildApiRestarting ? 'Waiting...' : 'Building...') : 'Close'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
